import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import type { ApkVersion, Application, Deployment, Device } from '../lib/api';
import {
  DeploymentStatusBadge,
  Empty,
  ErrorNotice,
  Loading,
  Notice,
  OnlineBadge,
  Panel,
  relativeTime,
  useAsync,
} from '../components/ui';

interface CreateResult {
  deploymentId: string;
  targetCount: number;
  onlineCount: number;
  offlineCount: number;
  incompatible: Array<{ deviceId: string; displayName: string; reason: string }>;
  message: string;
}

export default function DeploymentsPage() {
  const [params] = useSearchParams();
  const [apkVersionId, setApkVersionId] = useState(params.get('apk') ?? '');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [wallpaperFile, setWallpaperFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [result, setResult] = useState<CreateResult | null>(null);

  const apps = useAsync((signal) => api.get<{ applications: Application[] }>('/applications', signal), []);
  const devices = useAsync((signal) => api.get<{ devices: Device[] }>('/devices', signal), []);
  const list = useAsync(
    (signal) => api.get<{ deployments: Deployment[] }>('/deployments', signal),
    [],
    10_000,
  );

  // Flatten every version of every application into one selector, so a
  // deployment is "this exact build to these devices" rather than "the app".
  const versionsByApp = useAsync(
    async (signal) => {
      const applications = (await api.get<{ applications: Application[] }>('/applications', signal)).applications;
      const detail = await Promise.all(
        applications.map((a) =>
          api.get<{ application: Application; versions: ApkVersion[] }>(`/applications/${a.id}`, signal),
        ),
      );
      return detail;
    },
    [apps.data?.applications.length ?? 0],
  );

  const enrolled = (devices.data?.devices ?? []).filter((d) => d.enrollmentStatus === 'enrolled');

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    setSelected(selected.size === enrolled.length ? new Set() : new Set(enrolled.map((d) => d.id)));
  };

  const publish = async () => {
    if (!apkVersionId || selected.size === 0) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const form = new FormData();
      form.append('apkVersionId', apkVersionId);
      form.append('deviceIds', JSON.stringify([...selected]));
      if (wallpaperFile) form.append('wallpaper', wallpaperFile);
      const created = await api.upload<CreateResult>('/deployments', form);
      setResult(created);
      setSelected(new Set());
      setWallpaperFile(null);
      list.reload();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const chosenVersion = versionsByApp.data
    ?.flatMap((d) => d.versions.map((v) => ({ ...v, app: d.application })))
    .find((v) => v.id === apkVersionId);

  return (
    <>
      <div className="page-head">
        <div>
          <h2>Deployments</h2>
          <p>Publish a specific APK build to selected devices and track what actually happens.</p>
        </div>
      </div>

      <ErrorNotice error={error} />

      {result && (
        <Notice kind="info">
          <strong>{result.message}</strong>
          {result.incompatible.length > 0 && (
            <ul>
              {result.incompatible.map((i) => <li key={i.deviceId}>{i.reason}</li>)}
            </ul>
          )}
          <p style={{ margin: '8px 0 0' }}>
            <Link to={`/deployments/${result.deploymentId}`}>Track this deployment →</Link>
          </p>
        </Notice>
      )}

      <Panel title="New deployment">
        <label>
          <span>Application version</span>
          <select value={apkVersionId} onChange={(e) => setApkVersionId(e.target.value)}>
            <option value="">Select a build…</option>
            {(versionsByApp.data ?? []).map((entry) => (
              <optgroup key={entry.application.id} label={entry.application.display_name}>
                {entry.versions.map((v) => (
                  <option key={v.id} value={v.id}>
                    v{v.version_name} (code {v.version_code})
                    {v.min_sdk_version !== null ? ` · min API ${v.min_sdk_version}` : ''}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </label>

        {chosenVersion && (
          <p className="small muted" style={{ marginTop: -6 }}>
            <span className="mono">{chosenVersion.app.package_name}</span> · SHA-256{' '}
            <span className="mono">{chosenVersion.sha256.slice(0, 24)}…</span>
            {chosenVersion.min_sdk_version !== null && chosenVersion.min_sdk_version > 24 && (
              <span style={{ color: 'var(--danger)' }}>
                {' '}· requires API {chosenVersion.min_sdk_version}; Android 7 devices will be skipped
              </span>
            )}
          </p>
        )}

        <label style={{ marginTop: 12 }}>
          <span>Wallpaper (optional)</span>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
            onChange={(e) => setWallpaperFile(e.target.files?.[0] ?? null)}
          />
        </label>
        <p className="small muted" style={{ marginTop: -6 }}>
          {wallpaperFile
            ? `Will apply ${wallpaperFile.name} on the selected tablets (home and lock screen).`
            : 'If you skip this, tablets receive the default exam wallpaper. Policy is not changed.'}
        </p>

        <div style={{ marginTop: 12 }}>
          <div className="actions" style={{ marginBottom: 8 }}>
            <strong>Target devices</strong>
            <button className="small" onClick={selectAll}>
              {selected.size === enrolled.length && enrolled.length > 0 ? 'Clear all' : 'Select all'}
            </button>
            <span className="muted small">{selected.size} of {enrolled.length} selected</span>
          </div>

          {devices.loading && !devices.data ? (
            <Loading what="devices" />
          ) : enrolled.length === 0 ? (
            <Empty>No enrolled devices to deploy to.</Empty>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th style={{ width: 32 }} /><th>Device</th><th>Status</th><th>Android</th><th>Last seen</th></tr>
                </thead>
                <tbody>
                  {enrolled.map((d) => (
                    <tr key={d.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selected.has(d.id)}
                          onChange={() => toggle(d.id)}
                          aria-label={`Select ${d.displayName}`}
                        />
                      </td>
                      <td>{d.displayName}<div className="small muted">{d.model}</div></td>
                      <td><OnlineBadge state={d.online} /></td>
                      <td>{d.androidVersion} <span className="muted small">(API {d.androidSdkInt})</span></td>
                      <td className="small muted">{relativeTime(d.lastSeenAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="actions" style={{ marginTop: 12 }}>
          <button className="primary" onClick={publish} disabled={busy || !apkVersionId || selected.size === 0}>
            {busy ? 'Publishing…' : `Publish to ${selected.size} device(s)`}
          </button>
        </div>
        <p className="small muted" style={{ marginBottom: 0 }}>
          Publishing queues the install. Offline devices receive it when they reconnect, and no
          device is reported as installed until it confirms the package is present.
        </p>
      </Panel>

      <Panel title="Deployment history">
        {list.loading && !list.data ? (
          <Loading what="deployments" />
        ) : (list.data?.deployments.length ?? 0) === 0 ? (
          <Empty>No deployments yet.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Application</th><th>Version</th><th>Status</th>
                  <th className="num">Installed</th><th className="num">Failed</th>
                  <th className="num">In flight</th><th>Started</th><th>By</th>
                </tr>
              </thead>
              <tbody>
                {list.data!.deployments.map((d) => (
                  <tr key={d.id}>
                    <td>
                      <Link to={`/deployments/${d.id}`}>{d.application_name}</Link>
                      <div className="small muted mono">{d.package_name}</div>
                    </td>
                    <td>v{d.version_name}<div className="small muted">code {d.version_code}</div></td>
                    <td><DeploymentStatusBadge status={d.status} /></td>
                    <td className="num">{d.success_count}/{d.target_count}</td>
                    <td className="num" style={{ color: d.failure_count ? 'var(--danger)' : undefined }}>
                      {d.failure_count}
                    </td>
                    <td className="num">{d.in_flight_count ?? 0}</td>
                    <td className="small muted">{relativeTime(d.created_at)}</td>
                    <td className="small muted">{d.created_by_email ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </>
  );
}
